import { useSnowflakePwdAuthFabrics } from '@common/Fabric/SnowflakePwdAuthBanner/hooks';
import { SnowflakePwdAuthCountAlert } from '@common/Fabric/SnowflakePwdAuthBanner/SnowflakePwdAuthCountAlert';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { titleCase } from '@common/utils/metadataUtils';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { Container, Stack, TabPane, Tabs } from '@prophecy/ui-v3';
import { DefaultRecordType } from '@prophecy/ui-v3/Table/types';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { useIsSparkSupported, useEditionConfig } from 'frontend/core/src/context/edition';
import { isDebugMode } from 'frontend/core/src/utils/localstorage-keys';
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useGlobalVariables } from '../../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../../context/mixpanel/context';
import { useUser } from '../../../context/user';
import { useEntityTableSchema, getEntityTableSortConfigVariables } from '../components/Config';
import { EntityTable } from '../components/EntityTable';
import { Teams } from '../components/Teams';
import { UserPipelines } from '../components/UserPipelines';
import { Users } from '../components/Users';
import { SchemaType } from '../config/types';
import { UserPageLayout } from '../config/user';

const EntityTableWrap = styled(Stack)`
  overflow: hidden;
`;

const UserEntityDetail = ({ entity, parentEntity, uid }: { entity: Entity; parentEntity: Entity; uid: string }) => {
  const tableSchema = useEntityTableSchema(parentEntity, entity) as SchemaType<DefaultRecordType>;
  const { pwdAuthCount, snowflakeCount, canEditPwdAuthFabric } = useSnowflakePwdAuthFabrics();
  return (
    <EntityTableWrap height='100%' gap={theme.spaces.x20}>
      {entity === Entity.Fabric && (
        <SnowflakePwdAuthCountAlert
          entity='fabric'
          pwdAuthCount={pwdAuthCount}
          totalCount={snowflakeCount}
          canEdit={canEditPwdAuthFabric}
        />
      )}
      <EntityTable
        schema={tableSchema}
        variables={{ uid }}
        // @ts-ignore
        sortConfigVariables={getEntityTableSortConfigVariables(entity)}
      />
    </EntityTableWrap>
  );
};

export default function UserDetail() {
  const parentEntity = Entity.User;
  const { user } = useUser();
  const params = useParams<ExtractUrlPathVariables<typeof Private_Routes.User.tab.url>>();
  const uid = user?.id;
  const navigate = useNavigate();

  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents.common.metadata.entityOpen, {
      entity: parentEntity,
      name: user?.firstName,
      url: window.location.href
    });
  }, [track, user?.firstName, parentEntity]);

  const activeTab = titleCase(params.tab) || UserPageLayout.firstTab;
  const globalConf = useGlobalVariables();

  const changeTab = (tab: string) => {
    navigate(Private_Routes.User.tab.getUrl({ tab: tab.toLowerCase() }), { replace: true });
    track(EntityEvents.common.metadata.tabChange, {
      name: user?.firstName,
      tab,
      entity: parentEntity,
      url: window.location.href
    });
  };

  const _isDebugMode = isDebugMode();
  const isSQLProjectEnabled = globalConf.isSQLProjectEnabled;

  const isSparkSupported = useIsSparkSupported();
  const editionConfig = useEditionConfig();

  const isTabValid = (tab: (typeof UserPageLayout.tabs)[number]) => {
    const { entity } = tab;

    if (entity === Entity.Job && !isSparkSupported) {
      return false;
    }

    if (entity === Entity.Dataset && !isSparkSupported) {
      return false;
    }

    if (entity === Entity.Model) {
      return isSQLProjectEnabled && editionConfig.isModelEnabled;
    }

    if (entity === Entity.Subgraph) {
      return editionConfig.isSubgraphEnabled || _isDebugMode;
    }

    return true;
  };

  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x12} align='center' alignY='center'>
      <DocumentTitle title={`${parentEntity}:${user?.firstName || ''}`} />
      <Container noPadding={true}>
        <Tabs activeKey={activeTab} onChange={(tab) => changeTab(tab)}>
          {UserPageLayout.tabs.filter(isTabValid).map((tab) => {
            const title = tab.title;
            const entity = tab.entity;

            return (
              <TabPane key={title} tab={title}>
                {entity === Entity.Pipeline ? (
                  <UserPipelines />
                ) : entity === Entity.User ? (
                  <Users />
                ) : entity === Entity.Team ? (
                  <Teams uid={uid} />
                ) : (
                  <UserEntityDetail uid={uid} parentEntity={parentEntity} entity={entity} />
                )}
              </TabPane>
            );
          })}
        </Tabs>
      </Container>
    </Stack>
  );
}
